import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import type { TFunction } from 'i18next';
import React, { useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCamera, PiFile, PiFileArrowUp } from 'react-icons/pi';

import Button from '../Button/Button';

import { base64ToFile } from '@/utils/file';

interface DropzoneProps {
  onFileSelect?: (file: File) => void;
  maxSize?: number;
  type?: 'document' | 'csv';
  buttonText?: string;
  children?: React.ReactNode;
}

const accepted = {
  'csv': '.csv,text/csv',
  'document': '.pdf,.jpg,.jpeg,.png,.gif,.webp,application/pdf,image/jpeg,image/png,image/gif,image/webp'
}

const getSubtitles = (t: TFunction) => ({
  'csv': t('components.dropzone.subtitleCsv'),
  'document': t('components.dropzone.subtitleDocument')
}) 

const Dropzone: React.FC<DropzoneProps> = ({
  onFileSelect,
  maxSize = 5 * 1024 * 1024,
  type = 'document',
  buttonText,
  children
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resolvedButtonText = buttonText || t('components.dropzone.selectDocument');
  const subtitles = getSubtitles(t);

  const validateFile = (file: File): boolean => {
    if (file.size > maxSize) {
      setError(t('components.dropzone.fileTooLarge', { size: maxSize / 1024 / 1024 }));
      return false;
    }

    return true;
  };

  const handleFile = (file: File) => {
    if (validateFile(file)) {
      setError('');
      onFileSelect?.(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const showCamera = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
      });

      if (image.base64String) {
        const file = base64ToFile(
          image.base64String,
          `photo_${Date.now()}.${image.format}`,
          `image/${image.format}`
        );
        handleFile(file);
      }
    } catch (error) {
      setError(t('components.dropzone.cameraError'));
    }
  };

  const handleDropzoneClick = async () => {
    if (Capacitor.isNativePlatform() && type === 'document') {
      return
    }

    fileInputRef.current?.click();
  };

  return (
    <div
      className={`relative border-2 border-dashed rounded-lg w-full h-[15rem]
        ${isDragging ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}
        ${error ? 'border-red-500' : ''}
        cursor-pointer transition-colors duration-200`}
      onClick={handleDropzoneClick}
      onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
        accept={accepted[type]}
      />
      {Capacitor.isNativePlatform() && type === 'document'
        ? <NativeSelection
          showCamera={showCamera}
          showFileSelect={() => fileInputRef.current?.click()}
          error={error}
          t={t}
        >
          {children}
        </NativeSelection>
        : <WebSelection type={type} error={error} buttonText={resolvedButtonText} subtitles={subtitles}>
          {children}
        </WebSelection>
      }
    </div>
  );
};

interface NativeSelectionProps {
  error?: string,
  showCamera: () => void,
  showFileSelect: () => void,
  children?: React.ReactNode,
  t: TFunction
}

const NativeSelection: React.FC<NativeSelectionProps> = (
  { error, showCamera, showFileSelect, children, t }
) => {
  return (
    <div className='flex flex-col px-4 justify-center h-full'>
      {children}
      <Button
        variant='primary'
        className='py-[0.25rem] px-[1rem] h-9 mb-2 max-w'
        onClick={showCamera}>
        <span className='font-semibold text-white flex items-center gap-2'>
          <PiCamera className="h-6 w-6" />
          {t('components.dropzone.takePhoto')}
      </span>
      </Button>
      <Button
        variant='secondary'
        className='py-[0.25rem] px-[1rem] h-9'
        onClick={showFileSelect}>
        <span className='font-semibold text-dark-blue flex items-center gap-2'>
          <PiFile className="h-6 w-6" />
          {t('components.dropzone.selectFile')}
        </span>
      </Button>
      {error && (
        <p className="text-sm text-red-500 text-center mt-2">{error}</p>
      )}
    </div>
  )
}

interface WebSelectionProps {
  type: 'document' | 'csv',
  error?: string,
  buttonText?: string,
  children?: React.ReactNode,
  subtitles: Record<string, string>
}
const WebSelection: React.FC<WebSelectionProps> = ({
  type,
  error,
  buttonText,
  children,
  subtitles,
}) => {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
      {children}
      <div className="bg-blue-600 rounded-full p-4 pr-6 mb-2 flex flex-row gap-2">
        <PiFileArrowUp className="h-5 w-5 text-white" />
        <p className="text-gray-900 font-medium text-center text-white text-sm">
          {buttonText}
        </p>
      </div>
      <p className="text-gray-500 text-xs text-center">
        {subtitles[type]}
      </p>
      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}
  </div>
  )
}

export default Dropzone;
